// @ts-strict-ignore
import { Arrange } from "@fullscript/aviary-web";
import { useState } from "react";

import { getFingerprintHeader } from "@shared/helpers/getFingerprintHeader/getFingerprintHeader";
import { hasErrors } from "@shared/helpers/hasErrors";
import { logGaSignupEvent } from "@shared/helpers/logGaSignupEvent";
import { useCallistoPracOnboardingExperiment } from "@shared/hooks/useCallistoPracOnboardingExperiment/useCallistoPracOnboardingExperiment";
import { useFsAnalytics } from "@shared/hooks/useFsAnalytics/useFsAnalytics";
import { useIsRupaRedirectExperience } from "@shared/hooks/useIsRupaRedirectExperience/useIsRupaRedirectExperience";
import { useSharedGlobalConfig } from "@shared/hooks/useSharedGlobalConfig/useSharedGlobalConfig";
import { useSearchParams } from "@shared/react-router-dom/react-router-dom";
import type { PractitionerAdditionalAttributesType } from "@shared/types/PractitionerAdditionalAttributesType";
import { gRecaptchaExecute } from "@shared/utils/gRecaptchaExecute/gRecaptchaExecute";
import type { PractitionerSignUpUnauthenticatedSharedMutation as PractitionerSignUpMutationData } from "@unauthenticated/shared/data/mutations/PractitionerSignUp.mutation.generated";
import { usePractitionerSignUpUnauthenticatedSharedMutation as usePractitionerSignUpMutation } from "@unauthenticated/shared/data/mutations/PractitionerSignUp.mutation.generated";

import { SignUpPageSelector } from "./SignUpPageSelector";
import type { SignUpPageType, SimplifySignUpFormData } from "./types";

import * as styles from "./SimplifySignUp.styles";

interface SimplifySignUpProps {
  additionalAttributes?: PractitionerAdditionalAttributesType;
  isOAuth?: boolean;
  onCompleted: (data: PractitionerSignUpMutationData) => void;
  ssoErrors?: string;
}

const SimplifySignUp = ({
  additionalAttributes,
  isOAuth,
  onCompleted,
  ssoErrors,
}: SimplifySignUpProps) => {
  const pageVersion = "signup_with_t&c";
  const [searchParams, setSearchParams] = useSearchParams();
  const { storePlatform, isEmerson } = useSharedGlobalConfig();
  const { referredFirstName, referredLastName } =
    additionalAttributes?.peerReferralDisplayAttributes || {};

  const { logEvent } = useFsAnalytics();
  const isPracOnboardingEnabled = useCallistoPracOnboardingExperiment();
  const isRupaRedirectExperience = useIsRupaRedirectExperience();

  const [formData, setFormData] = useState<SimplifySignUpFormData>({
    firstName: referredFirstName ?? "",
    lastName: referredLastName ?? "",
    dispensaryName: "",
    practitionerType: null,
    tosTimestamp: "",
    email: "",
    password: "",
    storePlatform,
  });

  const [isAccountExists, setIsAccountExists] = useState(false);
  const [recaptchaError, setRecaptchaError] = useState(false);

  const signUpPage: SignUpPageType = isOAuth ? "oauth_sign_up_page" : "practitioner_sign_up_page";

  const isPageOneErrors = fields => {
    return fields?.firstName || fields?.lastName || fields?.practitionerTypeId;
  };

  const isPageTwoErrors = fields => {
    return fields?.email || fields?.password;
  };

  const [signUp, { loading, data }] = usePractitionerSignUpMutation({
    onCompleted: payload => {
      if (!hasErrors(payload?.auth?.practitionerSignUp?.errors)) {
        const { id, storeId } = payload?.auth?.practitionerSignUp?.practitioner || {};

        logGaSignupEvent(id, storeId, isEmerson);
      } else if (isPageOneErrors(payload?.auth?.practitionerSignUp?.errors?.fields)) {
        searchParams.set("step", "1");
        setSearchParams(searchParams);
      } else if (isPageTwoErrors(payload?.auth?.practitionerSignUp?.errors?.fields)) {
        // currently the only page two error from BE is duplicate email
        if (payload?.auth?.practitionerSignUp?.errors?.fields?.email) {
          setIsAccountExists(true);
        }
      }
      onCompleted(payload);
    },
  });

  const handleSubmit = async () => {
    const { firstName, lastName, email, password, practitionerType, dispensaryName, tosTimestamp } =
      formData;

    const {
      peerReferralDisplayAttributes: unusedPeerReferralDisplayAttributes,
      referralPartnerDisplayAttributes: unusedReferralPartnerDisplayAttributes,
      ...signUpOptionalAttributes
    } = {
      ...additionalAttributes,
    };

    const fingerprintHeader = await getFingerprintHeader();

    logEvent().authSignupSubmit({
      form_version: pageVersion,
      signup_method: "manual",
      referred_account: !!additionalAttributes?.peerReferralDisplayAttributes,
      destinations: ["mixpanel"],
    });

    gRecaptchaExecute(gToken => {
      signUp({
        variables: {
          input: {
            attributes: {
              firstName,
              lastName,
              storeName: dispensaryName,
              email,
              password,
              practitionerTypeId: practitionerType?.id || null,
              pageVersion,
              signUpPage,
              storePlatform,
              tosAcceptedAt: tosTimestamp,
              optional: {
                ...signUpOptionalAttributes,
                ...(isRupaRedirectExperience && { signupSource: "rupa_redirect" }),
              },
            },
            captchaToken: gToken,
          },
        },
        context: {
          headers: fingerprintHeader,
        },
      });
    }, setRecaptchaError);
  };

  return (
    <Arrange
      justify={isRupaRedirectExperience ? "center" : "spaceBetween"}
      alignment="center"
      isVertical
      isFullHeight={isRupaRedirectExperience}
      isFullWidth={isPracOnboardingEnabled || isRupaRedirectExperience}
      customCss={isRupaRedirectExperience ? styles.rupaRedirectArrange : undefined}
      data-testid="simplify-signup"
    >
      <SignUpPageSelector
        errors={data?.auth?.practitionerSignUp?.errors?.fields}
        isLoading={loading}
        formData={formData}
        setFormData={setFormData}
        onSubmit={handleSubmit}
        isAccountExists={isAccountExists}
        setIsAccountExists={setIsAccountExists}
        additionalAttributes={additionalAttributes}
        pageVersion={pageVersion}
        signUpPage={signUpPage}
        ssoErrors={ssoErrors}
        recaptchaError={recaptchaError}
      />
    </Arrange>
  );
};

export { SimplifySignUp };
